import React, { useCallback, useState } from 'react';
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  RouteComponentProps,
  useLocation,
} from 'react-router-dom';
import { Button, ShieldOutlineIcon, FilterIcon } from '@clearcover/atlas';
import { useAttributeTypes } from 'Shared/services/Reznor/GetAttributeTypes';
import { BlockListPanel } from './screens/BlockListPanel';
import { RulesPanel } from './screens/RulesPanel';
import { AddAttributeModal } from './components/AddAttributeModal';
import styles from './BlockList.module.css';

export const BlockList: React.FC<RouteComponentProps> = ({ match }) => {
  const location = useLocation();
  const [addAttributeOpen, setAddAttributeOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAddSuccess = useCallback(() => {
    setAddAttributeOpen(false);
    setRefreshKey((k) => k + 1);
  }, []);

  const isBlockListTab = !location.pathname.endsWith('/rules');

  // Single source of the attribute-type catalog for this page. Both the
  // listing filters and the Add modal consume it via props, so the
  // /blocklist/attribute_types endpoint is hit once instead of once per
  // consumer. Gated on the active tab so the Rules tab — which doesn't use
  // it — never triggers the request.
  const { data: attributeTypes } = useAttributeTypes(isBlockListTab);

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.titleBlock}>
          <h1 className={styles.heading}>Blocklist</h1>
          <p className={styles.subtitle}>
            Manage blocked attributes and the rules that match them
          </p>
        </div>
        {isBlockListTab && (
          <Button
            variant="primary"
            onClick={() => setAddAttributeOpen(true)}
            data-testid="block-list-add-attribute"
          >
            Add attribute
          </Button>
        )}
      </div>
      <nav className={styles.tabStrip} aria-label="Block List tabs">
        <NavLink
          exact
          to={`${match.url}/blocks`}
          className={styles.tab}
          activeClassName={styles.tabActive}
        >
          <ShieldOutlineIcon className={styles.tabIcon} />
          Block list
        </NavLink>
        <NavLink
          to={`${match.url}/rules`}
          className={styles.tab}
          activeClassName={styles.tabActive}
        >
          <FilterIcon className={styles.tabIcon} />
          Rules
        </NavLink>
      </nav>

      <div className={styles.content}>
        <Switch>
          <Redirect exact from={match.url} to={`${match.url}/blocks`} />
          <Route
            exact
            path={`${match.url}/blocks`}
            render={(props) => (
              <BlockListPanel
                {...props}
                refreshKey={refreshKey}
                attributeTypes={attributeTypes}
              />
            )}
          />
          <Route path={`${match.url}/rules`} component={RulesPanel} />
        </Switch>
      </div>

      <AddAttributeModal
        open={addAttributeOpen}
        onClose={() => setAddAttributeOpen(false)}
        onSuccess={handleAddSuccess}
        attributeTypes={attributeTypes}
      />
    </div>
  );
};
